import type { ComponentType, ReactNode } from 'react'
import type { Frame } from '../../shared/types'
import type { IntegrationsStatus } from '../lib/api'
import { figmaIntegration } from './figma'
import type { PlainMessageKey } from '../lib/i18n'
import { linearIntegration } from './linear'
import { canvaIntegration } from './canva'
import { slackIntegration } from './slack'
import { posthogIntegration } from './posthog'

/**
 * Client extensions, the mirror of `server/extensions.ts`: each integration
 * ships its own card for the Integrations page and (optionally) its section
 * in the canvas import modal. The pages iterate `clientIntegrations` and
 * never name an integration — removing one from a build is deleting its
 * folder and its line in the list below.
 */

export interface SettingsCardProps {
  status: IntegrationsStatus
  busy: boolean
  setBusy(busy: boolean): void
  setStatus(status: IntegrationsStatus): void
  showToast(message: string): void
  /** open the shared "Disconnect …?" confirm; the page calls `disconnect` */
  requestDisconnect(): void
}

export interface ImportSectionProps {
  canvasId: string
  /** swap the modal to a review view (null returns to this screen) */
  setReview(view: ReactNode | null): void
  /** the import started and runs on as a task — close the modal */
  onImporting(frameCount: number): void
  /** return to the source picker */
  onBack(): void
}

interface ClientIntegrationBase {
  id: string
  name: string
  /** the integration's mark on a paper tile (step chains, list rows) */
  Tile?: ComponentType<{ size?: number; className?: string }>
  /** its card in the import modal's source picker (with `ImportSection`) */
  importSource?: { title: string; blurb: string; icon: ReactNode }
  /** body copy of the shared disconnect confirm dialog, translated where it shows */
  disconnectCopy: PlainMessageKey
  isConnected(status: IntegrationsStatus): boolean
  disconnect(): Promise<IntegrationsStatus>
  SettingsCard: ComponentType<SettingsCardProps>
  ImportSection?: ComponentType<ImportSectionProps>
  /** drawn in the stage's world layer, over the frames (world coordinates) */
  StageOverlay?: ComponentType
  /** rows at the end of a frame's right-click menu (on its name, or on any
   *  element inside it); renders null to offer none */
  FrameMenuItem?: ComponentType<{ frame: Frame }>
}

/** An automation-capable integration must bring its own tile — step chains
 *  and list rows would otherwise have nothing honest to show for it. */
export type ClientIntegration =
  | (ClientIntegrationBase & { automationPull?: undefined })
  | (ClientIntegrationBase & {
      Tile: ComponentType<{ size?: number; className?: string }>
      automationPull: {
        /** the add-step tile's line, e.g. "Import what's new in a Figma file." */
        blurb: string
        /** what a pull lands, e.g. "Frames" */
        subtitle: string
        placeholder: string
        /** the source field on the step ('url' for Figma) */
        field: 'url'
      }
    })

export const clientIntegrations: ClientIntegration[] = [
  figmaIntegration,
  canvaIntegration,
  linearIntegration,
  slackIntegration,
  posthogIntegration,
]
